/**
 * Hook para desencriptar URLs de archivos protegidos con AES-256-GCM.
 * Si no hay metadata de encriptación, retorna la URL original (archivos legacy).
 */

import { useState, useEffect } from "react";
import {
  getDecryptedObjectUrl,
  revokeDecryptedUrl,
  type EncryptionMeta,
} from "@/lib/services/crypto";

interface UseDecryptedUrlOptions {
  /** URL del archivo (puede ser encriptado o legacy) */
  url: string | null | undefined;
  /** Metadata de encriptación. Si es null/undefined, se asume archivo legacy sin encriptar */
  encryption: EncryptionMeta | null | undefined;
  /** Content-type original del archivo (para reconstruir el blob desencriptado) */
  contentType?: string;
}

interface UseDecryptedUrlResult {
  /** URL lista para usar en <img src> o <a href>. Es un objectURL si está encriptado. */
  decryptedUrl: string | null;
  /** Si está en proceso de desencriptación */
  isDecrypting: boolean;
  /** Error durante la desencriptación */
  error: string | null;
}

export function useDecryptedUrl({
  url,
  encryption,
  contentType = "image/jpeg",
}: UseDecryptedUrlOptions): UseDecryptedUrlResult {
  const [decryptedUrl, setDecryptedUrl] = useState<string | null>(null);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setDecryptedUrl(null);
      setError(null);
      return;
    }

    // Archivo legacy sin encriptar → usar URL directamente
    if (!encryption) {
      setDecryptedUrl(url);
      setError(null);
      return;
    }

    let cancelled = false;

    async function decrypt() {
      setIsDecrypting(true);
      setError(null);
      try {
        const objectUrl = await getDecryptedObjectUrl(
          url!,
          encryption!.key,
          contentType,
        );
        if (!cancelled) {
          setDecryptedUrl(objectUrl);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error desencriptando archivo:", err);
          setError("No se pudo desencriptar el archivo");
          setDecryptedUrl(null);
        }
      } finally {
        if (!cancelled) {
          setIsDecrypting(false);
        }
      }
    }

    decrypt();

    return () => {
      cancelled = true;
      if (url && encryption) {
        revokeDecryptedUrl(url, encryption.key);
      }
    };
  }, [url, encryption?.key, contentType]);

  return { decryptedUrl, isDecrypting, error };
}
